// All simulation constants, per docs/TECH_SPEC.md §2.2 – §2.6.
// Every value is a 32-bit integer in FQ units (1 grid = 1024 FQ) unless noted.

/** Frozen per weekly track. Bump only when constants change. */
export const SIM_VERSION = '4';

// --- §2.2 fixed point & units -------------------------------------------------

export const FQ_SHIFT = 10;
export const FQ_ONE = 1 << FQ_SHIFT; // 1024 FQ = 1 grid
/** 1 grid = 2 display meters, so 1 meter = 512 FQ. */
export const FQ_PER_METER = 512;
/** Fixed 60 Hz. Reaching this frame alive = finished. */
export const MAX_FRAMES = 3600;
export const FRAMES_PER_SECOND = 60;

// --- §2.3 physics -------------------------------------------------------------

export const SPEED_START = 137;
export const SPEED_END = 273;
export const SPEED_RAMP_FRAMES = 2700;
export const GRAVITY = 12;
export const FAST_FALL_GRAVITY = 36;
export const JUMP_VELOCITY = 176;
export const COYOTE_FRAMES = 5;
export const INPUT_BUFFER_FRAMES = 6;
export const SLIDE_MAX_FRAMES = 24;
export const REVIVE_INVULN_FRAMES = 60;

export const HITBOX_STAND_W = 614;
export const HITBOX_STAND_H = 1843;
export const HITBOX_SLIDE_W = 819;
export const HITBOX_SLIDE_H = 922;

/** Player starts 4 grids into the track; obstacle x is absolute track x. */
export const PLAYER_START_X = 4096;
/** Falling into a gap kills once y drops below this. */
export const FALL_DEATH_Y = -2048;

// --- §2.4 obstacle geometry ---------------------------------------------------

export const SPIKE_W = 819;
/**
 * Spike box spans y in [0, SPIKE_H). 896 (0.875 g), not the visual 1 g: at 1024
 * the jump-timing window is only ~6 frames at start speed (difficulty inverted —
 * the first obstacle was the hardest input of the run). 896 widens the early
 * window to ~8 frames while keeping airtime and track spacing untouched.
 */
export const SPIKE_H = 896;

export const BAR_W = 512;
/** Bar box spans y in [BAR_BOTTOM, BAR_TOP). */
export const BAR_BOTTOM = 1126;
export const BAR_TOP = 3072;

export const GAP_W_MIN = 2048;
export const GAP_W_MAX = 3584;

// --- §2.7 input stream limits -------------------------------------------------

export const MAX_INPUT_EVENTS = 600;
export const MAX_EVENTS_PER_FRAME = 4;
export const MAX_REVIVES = 2;

// --- §2.6 track generation ----------------------------------------------------

/**
 * First obstacle at 5.0 s (v1: 3.0 s, v2/v3: 4.0 s). Second turn of the same
 * screw: production W35 measured firstObstacleProxy 31.5% > the design §2.6
 * 30% teaching-failure threshold, with p25 death frame sitting AT obstacle #1.
 * Bots (frame-perfect track knowledge) cannot see this; it is human prep time.
 */
export const TRACK_FIRST_FRAME = 300;
/**
 * Intervals before this frame get stretched — early-game breathing room.
 * v4 unifies the relief window with §2.3's 15 s teaching window
 * (= BAR_UNLOCK_FRAME; v3 had a 12 s/15 s split) and doubles the stretch
 * (×1.4 → ×2.0): the dose that moved mid-tier deathWindowShare into the
 * ≥45% band on the W36–W40 seeds (design §2.1: most deaths at 20–45 s).
 */
export const EARLY_RELIEF_FRAME = 900;
export const EARLY_RELIEF_NUM = 2;
export const EARLY_RELIEF_DEN = 1;
/**
 * No gaps before 10 s (new in v4). The gap is the unforgiving variant of the
 * stop-frame-taught jump (fall death, no near-miss), so the first demand is
 * always the taught spike; the type roll is still consumed, the result is
 * forced to 'spike' below this frame (§2.6). v3 allowed gaps from obstacle #1.
 */
export const GAP_UNLOCK_FRAME = 600;
/** Stop generating at 58 s. */
export const TRACK_LAST_FRAME = 3480;
/** intervalFrames(f) = INTERVAL_BASE - (INTERVAL_DROP * min(f, ramp) / ramp | 0) */
export const INTERVAL_BASE = 210;
export const INTERVAL_DROP = 144;
/** ± percent of the interval applied as PRNG jitter. */
export const JITTER_PERCENT = 20;
/** Bars (and combos) only unlock after 15 s. */
export const BAR_UNLOCK_FRAME = 900;
/** No bar within this many frames after the trailing edge of a gap. */
export const BAR_AFTER_GAP_FRAMES = 60;
export const COMBO_CHANCE_PERCENT = 25;
/** Combo partner sits airtime + this many frames behind the lead spike. */
export const COMBO_MARGIN_FRAMES = 6;
/** Extra frames on top of airtime required between separate action windows. */
export const MIN_SPACING_EXTRA_FRAMES = 12;

// --- §4bis.28 快斗 Blitz Duel: slice derivation -------------------------------
//
// These are NOT replay physics. Nothing in `Sim.step`, `generateTrack` or the
// distance tables reads any of them; they parameterise `slice.ts`, which does a
// read-only replay of an already-verified stream to decide where a duel starts.
// That is why adding them needs no SIM_VERSION bump and cannot move a golden —
// a property the sim suite asserts by name.

/** A source run must have reached this frame (20 s ≈ 390 m) to mint a slice.
 *  Above production's p50 death (919), below p75 (1863): the runs people share
 *  mint slices, sub-median noise does not. */
export const DUEL_MIN_SOURCE_DEATH = 1200;
/** Slice length, 12 s — shorter than the 15.3 s median opening it replaces. */
export const DUEL_SLICE_FRAMES = 720;
/** Never start inside the §2.3/§4bis.15 teaching window. */
export const DUEL_MIN_START_FRAME = 600;
/** The line to pass: the source's end + 1 s, "survive one second past my grave".
 *  Since §4bis.30 this is a MID-RUN beat, not the end of the run — passing it
 *  wins the duel and the challenger keeps running until they actually die. */
export const DUEL_SURVIVE_FRAMES = 60;
/** The first obstacle's action window must open at least this long after the
 *  handover: airtime 30 f + reaction ~15 f leaves 30 f of slack for a
 *  challenger who starts cold at ~2× opening speed with no runway. */
export const DUEL_CLEARANCE_FRAMES = 75;
/** How far either side of the ideal start the snap search may look. */
export const DUEL_SNAP_RANGE = 120;
/**
 * MINT GUARD (§4bis.30, replacing 004's `DUEL_SUFFIX_BUDGET = 120`). A duel no
 * longer stops at its win line, so the challenger's stream is an ordinary full
 * run's stream and the right cap is the one every run already has: the runtime
 * budget is `MAX_INPUT_EVENTS − prefixCount`, per slice, and `preValidate`
 * already enforces it. What still has to be guaranteed at MINT time is that the
 * budget left over is *enough* — hence a cap on the prefix rather than a
 * reservation for the suffix.
 *
 * 150 is a guarantee, not a constraint. Worst case (`startFrame = 600`, prefix
 * exactly at the cap) leaves ≥450 events for ≤3000 remaining frames = 1 per
 * 6.7 f, against the global cap's own 1 per 6 f. Measured on the five live
 * seeds with mid-tier bots: prefix p50 3, p90 10, max 13.
 *
 * 004 tested the WHOLE source stream against 480, counting events after the
 * handover that the prefix never uses — wrong, and it suppressed mints.
 */
export const DUEL_PREFIX_CAP = 150;
/** A death this soon after the handover is a start-line accident, not a duel. */
export const DUEL_MERCY_FRAMES = 60;

/**
 * 帮我 The hand-back (§4bis.31, iteration 006). A hand-back slice is derived by
 * the SAME `sliceForRun` as a challenge and differs in exactly one integer: the
 * ideal start is `E − HANDOFF_RUNWAY_FRAMES` instead of `winFrame −
 * DUEL_SLICE_FRAMES`. 打败我 hands you the 12 s that ended him; 帮我 drops you
 * 4 s short of his grave and asks you to carry the run on.
 *
 * Bounds, both load-bearing:
 *   - LOWER: it must exceed `DUEL_SNAP_RANGE + DUEL_MERCY_FRAMES = 180`, so even
 *     a snap to `E − 120` still leaves the grave a full 2 s past the mercy
 *     window — i.e. crossing it can never be scored as a start-line accident.
 *   - UPPER: far under `DUEL_SLICE_FRAMES = 720`, so a hand-back cannot read as
 *     another duel. The complaint that produced iteration 005 was that every
 *     hop looked like the previous one; a 300-frame slice to the line against a
 *     780-frame one is a visibly different ask.
 *
 * Like every `DUEL_*` above, this is a DERIVATION constant: nothing in
 * `Sim.step`, `generateTrack` or the distance tables reads it, so it changes
 * which slice a run mints and never what a stream does. No `SIM_VERSION` bump.
 */
export const HANDOFF_RUNWAY_FRAMES = 240;

/**
 * The event density the global cap itself assumes: `MAX_INPUT_EVENTS` events
 * over `MAX_FRAMES` frames = 1 per 6 frames. The hand-back mint guard reserves
 * exactly this for the track that remains (see `handoffMinBudget`).
 */
export const HANDOFF_BUDGET_FRAMES_PER_EVENT = 6;

// --- derived jump profile -----------------------------------------------------

/**
 * Exact per-frame jump heights, produced with the same integer integration the
 * sim uses (explicit Euler: y += vy; vy -= GRAVITY, both applied on the frame the
 * jump is pressed). JUMP_Y[k] is the height during frame `jumpFrame + k`, so
 * JUMP_Y[0] = JUMP_VELOCITY and the player is grounded again at
 * `jumpFrame + JUMP_AIRTIME_FRAMES`.
 */
function buildJumpProfile(): number[] {
  const ys: number[] = [];
  let y = 0;
  let vy = JUMP_VELOCITY;
  for (;;) {
    y = (y + vy) | 0;
    vy = (vy - GRAVITY) | 0;
    if (y <= 0) break;
    ys.push(y);
  }
  return ys;
}

export const JUMP_Y: readonly number[] = buildJumpProfile();

/** Frames spent above the ground, take-off frame included (30 ≈ 0.5 s). */
export const JUMP_AIRTIME_FRAMES = JUMP_Y.length;
/** Last frame offset at which the player is still above ground level (29). */
export const JUMP_LAST_AIR_FRAME = JUMP_AIRTIME_FRAMES - 1;
/** Highest point of a jump, FQ. */
export const JUMP_PEAK_Y = JUMP_Y.reduce((a, b) => (a > b ? a : b), 0);

function clearFirst(): number {
  for (let n = 0; n < JUMP_Y.length; n++) if (JUMP_Y[n]! >= SPIKE_H) return n;
  return -1;
}
function clearLast(): number {
  for (let n = JUMP_Y.length - 1; n >= 0; n--) if (JUMP_Y[n]! >= SPIKE_H) return n;
  return -1;
}

/** First / last frame offset of a jump at which the feet clear a spike (7 / 21). */
export const SPIKE_CLEAR_FIRST = clearFirst();
export const SPIKE_CLEAR_LAST = clearLast();

/** Minimum frames between the action windows of two separate obstacles (42). */
export const MIN_SPACING_FRAMES = JUMP_AIRTIME_FRAMES + MIN_SPACING_EXTRA_FRAMES;
/** Frames between the two halves of a combo (36). */
export const COMBO_SPACING_FRAMES = JUMP_AIRTIME_FRAMES + COMBO_MARGIN_FRAMES;
/** A gap must be crossable in at most this many airborne frames (25). */
export const MAX_GAP_CROSS_FRAMES = JUMP_AIRTIME_FRAMES - 5;
